/** Shared hooks. */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

/** Debounces a rapidly-changing value, e.g. a search box. */
export function useDebounced<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

/** Reveals an element once it first enters the viewport. Returns `true` immediately when the visitor prefers reduced motion. */
export function useReveal<T extends HTMLElement = HTMLDivElement>(): [React.RefObject<T>, boolean] {
  const ref = useRef<T>(null);
  const prefersReduced =
    typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const [visible, setVisible] = useState(Boolean(prefersReduced));

  useEffect(() => {
    if (visible || !ref.current || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.05 },
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [visible]);

  return [ref, visible];
}

/** Closes an overlay when a click lands outside it. */
export function useClickOutside<T extends HTMLElement>(onOutside: () => void, active = true): React.RefObject<T> {
  const ref = useRef<T>(null);
  useEffect(() => {
    if (!active) return;
    function handler(event: MouseEvent | TouchEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) onOutside();
    }
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [onOutside, active]);
  return ref;
}

/** Calls a handler when `Escape` is pressed. */
export function useEscapeKey(onEscape: () => void, active = true): void {
  useEffect(() => {
    if (!active) return;
    function handler(event: KeyboardEvent) {
      if (event.key === 'Escape') onEscape();
    }
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onEscape, active]);
}

/** Reports whether the page has been scrolled past a threshold — used to condense the header. */
export function useScrolled(threshold = 12): boolean {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    let frame = 0;
    function onScroll() {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setScrolled(window.scrollY > threshold));
    }
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(frame);
    };
  }, [threshold]);
  return scrolled;
}

/** Locks body scroll while an overlay is open, restoring the previous value on close. */
export function useScrollLock(locked: boolean): void {
  useEffect(() => {
    if (!locked) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [locked]);
}

/**
 * Filter state mirrored in the query string, so a filtered view is shareable, bookmarkable and
 * survives the back button.
 */
export function useQueryFilters<T extends Record<string, string | undefined>>(defaults: T) {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = { ...defaults } as T;
  for (const key of Object.keys(defaults)) {
    const value = searchParams.get(key);
    if (value !== null) (filters as Record<string, string>)[key] = value;
  }

  const setFilter = useCallback(
    (key: keyof T & string, value: string | undefined) => {
      const next = new URLSearchParams(searchParams);
      if (!value || value === defaults[key]) next.delete(key);
      else next.set(key, value);
      if (key !== 'page') next.delete('page');
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams, defaults],
  );

  const clearFilters = useCallback(() => setSearchParams(new URLSearchParams(), { replace: true }), [setSearchParams]);

  const activeCount = Object.keys(defaults).filter(
    (key) => key !== 'page' && searchParams.get(key) && searchParams.get(key) !== defaults[key],
  ).length;

  return { filters, setFilter, clearFilters, activeCount };
}

/** Scrolls to the top on navigation, but respects in-page anchors and back-button restoration. */
export function useScrollRestoration(): void {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const target = document.getElementById(hash.slice(1));
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname, hash]);
}

/** Persists a value in `localStorage`, tolerating environments where storage is unavailable. */
export function useLocalStorage<T>(key: string, initial: T): [T, (value: T) => void] {
  const [stored, setStored] = useState<T>(() => {
    try {
      const raw = window.localStorage.getItem(key);
      return raw ? (JSON.parse(raw) as T) : initial;
    } catch {
      return initial;
    }
  });

  const set = useCallback(
    (value: T) => {
      setStored(value);
      try {
        window.localStorage.setItem(key, JSON.stringify(value));
      } catch {
        // Private browsing or a full quota: keeping the in-memory value is the right fallback.
      }
    },
    [key],
  );

  return [stored, set];
}
